import React, { useEffect, useState } from 'react'
import {
  Box,
  Container,
  Paper,
  Typography,
  Button,
  IconButton,
  TextField,
  Checkbox,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  LinearProgress,
  Chip,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Divider,
  InputAdornment,
  List,
  ListItem,
  ListItemText,
  CircularProgress,
  Card,
  CardContent,
  Fade,
  Slide,
} from '@mui/material'
import { styled } from '@mui/material/styles'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import AddIcon from '@mui/icons-material/Add'
import DeleteIcon from '@mui/icons-material/Delete'
import EditIcon from '@mui/icons-material/Edit'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked'
import LibraryAddIcon from '@mui/icons-material/LibraryAdd'
import SaveIcon from '@mui/icons-material/Save'
import CloseIcon from '@mui/icons-material/Close'
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh'
import CompareArrowsIcon from '@mui/icons-material/CompareArrows'
import {
  makeGetAllRequest,
  makePostRequest,
  makePatchRequest,
  makeDeleteRequest,
} from '../../utils/api'
import urls from '../../utils/urls'

// Styled components for dark theme UI (SmartFeed style)
const StyledPaper = styled(Paper)(({ theme }) => ({
  borderRadius: 16,
  background: 'linear-gradient(145deg, #1e1e2e 0%, #252540 100%)',
  boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
  transition: 'all 0.3s ease',
  border: '1px solid rgba(255,255,255,0.05)',
  '&:hover': {
    boxShadow: '0 12px 40px rgba(0,0,0,0.4)',
    border: '1px solid rgba(255,255,255,0.1)',
  },
}))

const StyledAccordion = styled(Accordion)(({ theme }) => ({
  borderRadius: '16px !important',
  marginBottom: theme.spacing(2),
  background: 'linear-gradient(145deg, #1e1e2e 0%, #252540 100%)',
  boxShadow: '0 4px 20px rgba(0,0,0,0.3)',
  border: '1px solid rgba(255,255,255,0.05)',
  '&:before': {
    display: 'none',
  },
  '&.Mui-expanded': {
    margin: `${theme.spacing(2)} 0 !important`,
  },
}))

const ItemCard = styled(Card)(({ theme, bought }) => ({
  marginBottom: theme.spacing(1.5),
  borderRadius: 12,
  background: bought 
    ? 'linear-gradient(145deg, rgba(76, 175, 80, 0.15) 0%, rgba(76, 175, 80, 0.08) 100%)'
    : 'linear-gradient(145deg, #2a2a3e 0%, #1e1e2e 100%)',
  border: bought ? '2px solid rgba(76, 175, 80, 0.5)' : '1px solid rgba(255,255,255,0.08)',
  boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
  transition: 'all 0.2s ease',
  '&:hover': {
    transform: 'translateX(4px)',
    boxShadow: '0 4px 16px rgba(0,0,0,0.3)',
    border: bought ? '2px solid rgba(76, 175, 80, 0.7)' : '1px solid rgba(255,255,255,0.15)',
  },
}))

const AddButton = styled(Button)(({ theme }) => ({
  borderRadius: 20,
  textTransform: 'none',
  fontWeight: 600,
  padding: '10px 24px',
  boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
  color: 'white',
  border: 'none',
  '&:hover': {
    boxShadow: '0 6px 20px rgba(102, 126, 234, 0.4)',
    background: 'linear-gradient(135deg, #7e8ef5 0%, #8b5db8 100%)',
  },
  '&.MuiButton-outlined': {
    background: 'transparent',
    border: '2px dashed rgba(102, 126, 234, 0.5)',
    color: '#667eea',
    '&:hover': {
      background: 'rgba(102, 126, 234, 0.1)',
      border: '2px dashed rgba(102, 126, 234, 0.8)',
    },
  },
}))

const ShoppingListDetail = ({ list, onBack, onUpdate, showSnackbar }) => {
  const [items, setItems] = useState([])
  const [loading, setLoading] = useState(true)
  const [groupedItems, setGroupedItems] = useState({})
  const [listStatus, setListStatus] = useState(list.status)
  const [editingItem, setEditingItem] = useState(null)
  const [newItem, setNewItem] = useState({
    name: '',
    quantity: 1,
    unit: 'pcs',
    aisle: '',
  })
  const [showAddFromLibrary, setShowAddFromLibrary] = useState(false)
  const [libraryItems, setLibraryItems] = useState([])
  const [selectedLibraryItems, setSelectedLibraryItems] = useState([])
  const [filter, setFilter] = useState('all') // all, remaining, bought
  const [showAddAisleDialog, setShowAddAisleDialog] = useState(false)
  const [newAisleName, setNewAisleName] = useState('')
  const [showAddItemDialog, setShowAddItemDialog] = useState(false)
  const [selectedAisle, setSelectedAisle] = useState('')
  const [editingItemData, setEditingItemData] = useState({
    name: '',
    quantity: 1,
    unit: 'pcs'
  })
  const [showRationalizeDialog, setShowRationalizeDialog] = useState(false)
  const [rationalizeLoading, setRationalizeLoading] = useState(false)
  const [rationalizePreview, setRationalizePreview] = useState(null)

  const units = ['pcs', 'kg', 'g', 'L', 'ml', 'lb', 'oz', 'cup', 'tbsp', 'tsp', 'package', 'bottle', 'can', 'box', 'bag']

  useEffect(() => {
    fetchItems()
    fetchLibraryItems()
  }, [list.id])

  // Auto-update status when items change (with debounce to avoid infinite loops)
  useEffect(() => {
    if (items.length > 0) {
      const timer = setTimeout(() => {
        autoUpdateListStatus()
      }, 300)
      return () => clearTimeout(timer)
    }
  }, [items.filter(i => i.status === 'bought').length, items.length])

  useEffect(() => {
    groupItemsByAisle()
  }, [items, filter])

  // `silent` : rafraîchir SANS repasser par l'état loading. Le bloc de la
  // liste est monté sous `{!loading && ...}` : lever le drapeau démonte tout
  // le contenu, la page perd sa hauteur, et le navigateur ramène le scroll en
  // haut. C'est ce qui donnait le « ça remonte » à chaque coche.
  const fetchItems = async ({ silent = false } = {}) => {
    if (!silent) setLoading(true)
    try {
      // makeGetAllRequest et pas makeGetRequest : le backend pagine à 20 et
      // une liste de courses en compte couramment 40+.
      const response = await makeGetAllRequest(
        `${urls.shoppingItems}?list_id=${list.id}`,
        {},
        true,
      )
      if (response?.status === 200) {
        setItems(response.data.results || [])
      }
    } catch (error) {
      console.error('Error fetching items:', error)
      showSnackbar('Erreur lors du chargement des articles', 'error')
    } finally {
      if (!silent) setLoading(false)
    }
  }

  const fetchLibraryItems = async () => {
    try {
      const response = await makeGetAllRequest(urls.itemLibrary, {}, true)
      if (response?.status === 200) {
        setLibraryItems(response.data.results || [])
      }
    } catch (error) {
      console.error('Error fetching library items:', error)
    }
  }

  const groupItemsByAisle = () => {
    let filteredItems = items
    
    if (filter === 'remaining') {
      filteredItems = items.filter(item => item.status === 'needed')
    } else if (filter === 'bought') {
      filteredItems = items.filter(item => item.status === 'bought')
    }

    const grouped = filteredItems.reduce((acc, item) => {
      const aisle = item.aisle || 'Other'
      if (!acc[aisle]) {
        acc[aisle] = []
      }
      acc[aisle].push(item)
      return acc
    }, {})
    
    setGroupedItems(grouped)
  }

  const handleAddItem = async (aisleOverride = null) => {
    const itemName = showAddItemDialog ? editingItemData.name : newItem.name
    const itemQuantity = showAddItemDialog ? editingItemData.quantity : newItem.quantity
    const itemUnit = showAddItemDialog ? editingItemData.unit : newItem.unit
    const itemAisle = aisleOverride || selectedAisle || newItem.aisle

    if (!itemName.trim()) {
      showSnackbar('Le nom de l\'article est requis', 'error')
      return
    }

    try {
      const response = await makePostRequest(urls.shoppingItems, {
        name: itemName,
        quantity: itemQuantity,
        unit: itemUnit,
        aisle: itemAisle,
        list: list.id,
        status: 'needed',
      }, {}, true)
      
      if (response?.status === 201) {
        await fetchItems({ silent: true })
        setNewItem({ name: '', quantity: 1, unit: 'pcs', aisle: '' })
        setEditingItemData({ name: '', quantity: 1, unit: 'pcs' })
        setShowAddItemDialog(false)
        setSelectedAisle('')
        showSnackbar('Article ajouté avec succès !', 'success')
      }
    } catch (error) {
      console.error('Error adding item:', error)
      showSnackbar('Erreur lors de l\'ajout de l\'article', 'error')
    }
  }

  const handleCreateAisle = async () => {
    if (newAisleName.trim()) {
      // Create a temporary item in the new aisle so it shows up
      try {
        const response = await makePostRequest(urls.shoppingItems, {
          name: '_placeholder_',
          quantity: 1,
          unit: 'pcs',
          aisle: newAisleName,
          list: list.id,
          status: 'needed',
        }, {}, true)
        
        if (response?.status === 201) {
          // Immediately delete the placeholder
          await makeDeleteRequest(`${urls.shoppingItems}${response.data.id}/`, {}, true)
          
          // Set the new aisle for next item
          setNewItem({ ...newItem, aisle: newAisleName })
          setShowAddAisleDialog(false)
          
          // Refresh to show the empty aisle
          await fetchItems({ silent: true })
          
          // Open dialog to add first item
          setSelectedAisle(newAisleName)
          setEditingItemData({ name: '', quantity: 1, unit: 'pcs' })
          setShowAddItemDialog(true)
          
          setNewAisleName('')
          showSnackbar(`Rayon "${newAisleName}" créé! Ajoutez des articles maintenant.`, 'success')
        }
      } catch (error) {
        console.error('Error creating aisle:', error)
        // Fallback: just set it for next item
        setNewItem({ ...newItem, aisle: newAisleName })
        setShowAddAisleDialog(false)
        setNewAisleName('')
        showSnackbar(`Rayon "${newAisleName}" créé! Ajoutez des articles maintenant.`, 'success')
      }
    }
  }

  const handleOpenAddItemToAisle = (aisle) => {
    setSelectedAisle(aisle)
    setEditingItemData({ name: '', quantity: 1, unit: 'pcs' })
    setShowAddItemDialog(true)
  }

  // Cocher un article ne doit RIEN démonter : on met l'état à jour localement
  // (optimiste) puis on envoie le PATCH. Pas de refetch, donc pas de spinner,
  // donc pas de retour en haut de page. En cas d'échec on remet la ligne dans
  // son état d'origine et on prévient.
  //
  // Le statut de la liste est déjà recalculé par le useEffect qui observe
  // items — inutile d'appeler autoUpdateListStatus ici.
  const handleToggleItem = async (item) => {
    const newStatus = item.status === 'needed' ? 'bought' : 'needed'
    const previousStatus = item.status

    const applyStatus = (statusValue) => {
      setItems((prev) =>
        prev.map((row) => (row.id === item.id ? { ...row, status: statusValue } : row)),
      )
    }

    applyStatus(newStatus)

    try {
      const response = await makePatchRequest(`${urls.shoppingItems}${item.id}/`, {
        status: newStatus,
      }, {}, true)

      if (response?.status === 200) {
        onUpdate()
      } else {
        applyStatus(previousStatus)
        showSnackbar('Erreur lors de la mise à jour de l\'article', 'error')
      }
    } catch (error) {
      console.error('Error updating item:', error)
      applyStatus(previousStatus)
      showSnackbar('Erreur lors de la mise à jour de l\'article', 'error')
    }
  }

  const handleStartEditItem = (item) => {
    setEditingItem(item.id)
    setEditingItemData({
      name: item.name,
      quantity: item.quantity,
      unit: item.unit
    })
  }

  const handleSaveEditItem = async (itemId) => {
    try {
      // Check if all fields are empty - delete the item
      if (!editingItemData.name.trim() && !editingItemData.quantity) {
        await handleDeleteItem(itemId)
        return
      }

      const response = await makePatchRequest(
        `${urls.shoppingItems}${itemId}/`, 
        editingItemData, 
        {}, 
        true
      )
      
      if (response?.status === 200) {
        // Même raison que pour le toggle : on remplace la ligne sur place au
        // lieu de recharger toute la liste.
        setItems((prev) =>
          prev.map((row) => (row.id === itemId ? { ...row, ...response.data } : row)),
        )
        setEditingItem(null)
        showSnackbar('Article mis à jour avec succès !', 'success')
      }
    } catch (error) {
      console.error('Error updating item:', error)
      showSnackbar('Error updating item', 'error')
    }
  }

  const handleCancelEdit = () => {
    setEditingItem(null)
    setEditingItemData({ name: '', quantity: 1, unit: 'pcs' })
  }

  const handleDeleteItem = async (itemId) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer cet article ?')) {
      try {
        const response = await makeDeleteRequest(`${urls.shoppingItems}${itemId}/`, {}, true)
        
        if (response?.status === 204) {
          setItems((prev) => prev.filter((row) => row.id !== itemId))
          onUpdate()
          showSnackbar('Article supprimé avec succès !', 'success')
        }
      } catch (error) {
        console.error('Error deleting item:', error)
        showSnackbar('Erreur lors de la suppression de l\'article', 'error')
      }
    }
  }

  // Calculate what the status should be based on items
  const calculateAutoStatus = () => {
    if (items.length === 0) return 'to_do'
    
    const boughtCount = items.filter(item => item.status === 'bought').length
    const totalCount = items.length
    
    if (boughtCount === 0) {
      return 'to_do'
    } else if (boughtCount === totalCount) {
      return 'completed'
    } else {
      return 'in_progress'
    }
  }

  // Auto-update list status based on items (system suggestion)
  const autoUpdateListStatus = async () => {
    const suggestedStatus = calculateAutoStatus()
    
    // Only auto-update if the status would change
    if (suggestedStatus !== listStatus) {
      try {
        const response = await makePatchRequest(`${urls.shoppingLists}${list.id}/`, {
          status: suggestedStatus,
        }, {}, true)
        
        if (response?.status === 200) {
          setListStatus(suggestedStatus)
        }
      } catch (error) {
        console.error('Error auto-updating list status:', error)
      }
    }
  }

  // Manual status update (user override)
  const handleUpdateListStatus = async (newStatus) => {
    try {
      const response = await makePatchRequest(`${urls.shoppingLists}${list.id}/`, {
        status: newStatus,
      }, {}, true)
      
      if (response?.status === 200) {
        setListStatus(newStatus)
        onUpdate()
        showSnackbar('Statut de la liste mis à jour !', 'success')
      }
    } catch (error) {
      console.error('Error updating list status:', error)
      showSnackbar('Erreur lors de la mise à jour du statut de la liste', 'error')
    }
  }

  const handleAddFromLibrary = async () => {
    if (selectedLibraryItems.length === 0) {
      showSnackbar('Veuillez sélectionner des articles à ajouter', 'error')
      return
    }

    try {
      const response = await makePostRequest(
        `${urls.shoppingLists}${list.id}/add_from_library/`,
        { library_item_ids: selectedLibraryItems },
        {},
        true
      )
      
      if (response?.status === 201) {
        await fetchItems({ silent: true })
        setShowAddFromLibrary(false)
        setSelectedLibraryItems([])
        showSnackbar('Articles ajoutés depuis la bibliothèque !', 'success')
      }
    } catch (error) {
      console.error('Error adding from library:', error)
      showSnackbar('Erreur lors de l\'ajout des articles depuis la bibliothèque', 'error')
    }
  }

  const handleRationalizeItems = async () => {
    setRationalizeLoading(true)
    setShowRationalizeDialog(true)
    setRationalizePreview(null)

    try {
      const response = await makePostRequest(
        `${urls.shoppingLists}${list.id}/rationalize_items/`,
        {},
        {},
        true
      )
      
      if (response?.status === 200) {
        setRationalizePreview(response.data)
      } else {
        showSnackbar('Échec de la rationalisation des articles', 'error')
        setShowRationalizeDialog(false)
      }
    } catch (error) {
      console.error('Error rationalizing items:', error)
      showSnackbar(error.response?.data?.error || 'Erreur lors de la rationalisation des articles', 'error')
      setShowRationalizeDialog(false)
    } finally {
      setRationalizeLoading(false)
    }
  }

  const handleApplyRationalization = async () => {
    if (!rationalizePreview?.rationalized_items) {
      showSnackbar('Aucune donnée de rationalisation disponible', 'error')
      return
    }

    setRationalizeLoading(true)

    try {
      const response = await makePostRequest(
        `${urls.shoppingLists}${list.id}/apply_rationalization/`,
        { rationalized_items: rationalizePreview.rationalized_items },
        {},
        true
      )
      
      if (response?.status === 200) {
        await fetchItems({ silent: true })
        setShowRationalizeDialog(false)
        setRationalizePreview(null)
        showSnackbar('Articles rationalisés avec succès !', 'success')
        onUpdate()
      }
    } catch (error) {
      console.error('Error applying rationalization:', error)
      showSnackbar('Erreur lors de l\'application de la rationalisation', 'error')
    } finally {
      setRationalizeLoading(false)
    }
  }

  const calculateProgress = () => {
    if (items.length === 0) return 0
    const boughtCount = items.filter(item => item.status === 'bought').length
    return (boughtCount / items.length) * 100
  }

  const progress = calculateProgress()

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#0f0f1e', pb: 4 }}>
      <Container maxWidth="lg" sx={{ pt: 4 }}>
        {/* Header */}
        <Box sx={{ mb: 3 }}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={onBack}
            sx={{ 
              mb: 2,
              borderRadius: 20,
              textTransform: 'none',
              fontWeight: 600,
              color: 'white',
              '&:hover': {
                bgcolor: 'rgba(255,255,255,0.1)',
              },
            }}
          >
            Retour aux listes
          </Button>
          
          <StyledPaper sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2, mb: 3 }}>
              <Typography variant="h4" sx={{ fontWeight: 700, color: 'white' }}>
                {list.name}
              </Typography>
            
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
              <AddButton
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => setShowAddAisleDialog(true)}
                sx={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}
              >
                Nouveau Rayon
              </AddButton>
              <AddButton
                variant="contained"
                startIcon={<AutoFixHighIcon />}
                onClick={handleRationalizeItems}
                disabled={items.filter(i => i.status === 'needed').length === 0}
                sx={{ 
                  background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #f5a3ff 0%, #ff6b7f 100%)',
                  },
                  '&:disabled': {
                    background: 'rgba(255,255,255,0.1)',
                    color: 'rgba(255,255,255,0.3)',
                  }
                }}
              >
                Rationaliser
              </AddButton>
              <FormControl size="small" sx={{ minWidth: 150 }}>
                <InputLabel sx={{ color: 'rgba(255,255,255,0.7)' }}>Statut</InputLabel>
                <Select
                  value={listStatus}
                  label="Statut"
                  onChange={(e) => handleUpdateListStatus(e.target.value)}
                  sx={{ 
                    borderRadius: 3,
                    bgcolor: 'rgba(255,255,255,0.05)',
                    color: 'white',
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: 'rgba(255,255,255,0.15)',
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: 'rgba(255,255,255,0.25)',
                    },
                    '& .MuiSvgIcon-root': {
                      color: 'white',
                    },
                  }}
                >
                  <MenuItem value="to_do">À faire</MenuItem>
                  <MenuItem value="in_progress">En cours</MenuItem>
                  <MenuItem value="completed">Terminé</MenuItem>
                </Select>
              </FormControl>
            </Box>
          </Box>

          {/* Progress Bar */}
          <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="body2" fontWeight={600} sx={{ color: 'rgba(255,255,255,0.7)' }}>
                Progression des achats
              </Typography>
              <Typography variant="body2" fontWeight={700} sx={{ color: '#667eea' }}>
                {items.filter(i => i.status === 'bought').length} / {items.length} articles ({progress.toFixed(0)}%)
              </Typography>
            </Box>
            <LinearProgress 
              variant="determinate" 
              value={progress} 
              sx={{ 
                height: 12, 
                borderRadius: 6,
                backgroundColor: 'rgba(255,255,255,0.1)',
                '& .MuiLinearProgress-bar': {
                  borderRadius: 6,
                  background: 'linear-gradient(90deg, #4caf50 0%, #8bc34a 100%)',
                }
              }}
            />
          </Box>
        </StyledPaper>
      </Box>

      {/* Filter Buttons */}
      <Box sx={{ mb: 3, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
        <Chip 
          label={`Tous (${items.length})`}
          onClick={() => setFilter('all')}
          clickable
          sx={{ 
            borderRadius: 20, 
            fontWeight: 600,
            px: 2,
            bgcolor: filter === 'all' ? '#667eea' : 'rgba(255,255,255,0.08)',
            color: filter === 'all' ? 'white' : 'rgba(255,255,255,0.7)',
            border: '1px solid rgba(255,255,255,0.1)',
            '&:hover': { 
              transform: 'scale(1.05)',
              bgcolor: filter === 'all' ? '#7e8ef5' : 'rgba(255,255,255,0.15)',
            },
            transition: 'all 0.2s'
          }}
        />
        <Chip 
          label={`Restants (${items.filter(i => i.status === 'needed').length})`}
          onClick={() => setFilter('remaining')}
          clickable
          sx={{ 
            borderRadius: 20, 
            fontWeight: 600,
            px: 2,
            bgcolor: filter === 'remaining' ? '#ff9800' : 'rgba(255,255,255,0.08)',
            color: filter === 'remaining' ? 'white' : 'rgba(255,255,255,0.7)',
            border: '1px solid rgba(255,255,255,0.1)',
            '&:hover': { 
              transform: 'scale(1.05)',
              bgcolor: filter === 'remaining' ? '#ffa726' : 'rgba(255,255,255,0.15)',
            },
            transition: 'all 0.2s'
          }}
        />
        <Chip 
          label={`Achetés (${items.filter(i => i.status === 'bought').length})`}
          onClick={() => setFilter('bought')}
          clickable
          sx={{ 
            borderRadius: 20, 
            fontWeight: 600,
            px: 2,
            bgcolor: filter === 'bought' ? '#4caf50' : 'rgba(255,255,255,0.08)',
            color: filter === 'bought' ? 'white' : 'rgba(255,255,255,0.7)',
            border: '1px solid rgba(255,255,255,0.1)',
            '&:hover': { 
              transform: 'scale(1.05)',
              bgcolor: filter === 'bought' ? '#66bb6a' : 'rgba(255,255,255,0.15)',
            },
            transition: 'all 0.2s'
          }}
        />
      </Box>


      {/* Loading State */}
      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <CircularProgress />
        </Box>
      )}

      {/* Items Grouped by Aisle */}
      {!loading && (
        <Box>
          {Object.keys(groupedItems).length === 0 ? (
            <StyledPaper sx={{ p: 6, textAlign: 'center' }}>
              <Typography variant="h6" sx={{ color: 'rgba(255,255,255,0.7)' }} gutterBottom>
                Aucun article dans cette liste
              </Typography>
              <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.5)' }}>
                Créez un rayon et ajoutez des articles pour commencer vos courses !
              </Typography>
              <Box sx={{ mt: 3, display: 'flex', gap: 2, justifyContent: 'center' }}>
                <AddButton
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={() => setShowAddAisleDialog(true)}
                >
                  Créer un rayon
                </AddButton>
              </Box>
            </StyledPaper>
          ) : (
            <>
              {Object.entries(groupedItems).map(([aisle, aisleItems]) => (
                <StyledAccordion key={aisle} defaultExpanded>
                  <AccordionSummary 
                    expandIcon={<ExpandMoreIcon sx={{ color: 'white' }} />}
                    sx={{ 
                      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                      borderRadius: '16px 16px 0 0',
                      '&.Mui-expanded': {
                        minHeight: 48,
                      },
                    }}
                  >
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', pr: 2, alignItems: 'center' }}>
                      <Typography variant="h6" sx={{ color: 'white', fontWeight: 700 }}>
                        {aisle}
                      </Typography>
                      <Chip 
                        label={`${aisleItems.length} article${aisleItems.length > 1 ? 's' : ''}`} 
                        size="small"
                        sx={{ 
                          backgroundColor: 'rgba(255,255,255,0.25)', 
                          color: 'white',
                          fontWeight: 600,
                          border: '1px solid rgba(255,255,255,0.3)',
                        }}
                      />
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails sx={{ p: 2, backgroundColor: 'transparent' }}>
                  <List>
                    {aisleItems.map((item) => (
                      <ItemCard key={item.id} bought={item.status === 'bought' ? 1 : 0}>
                        <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                          {editingItem === item.id ? (
                            <Grid container spacing={1.5} alignItems="center">
                              <Grid item xs={12} sm={5}>
                                <TextField
                                  fullWidth
                                  size="small"
                                  value={editingItemData.name}
                                  onChange={(e) => setEditingItemData({ ...editingItemData, name: e.target.value })}
                                  autoFocus
                                  placeholder="Nom de l'article"
                                  sx={{ 
                                    '& .MuiOutlinedInput-root': { 
                                      borderRadius: 2,
                                      bgcolor: 'rgba(255,255,255,0.05)',
                                      color: 'white',
                                      '& fieldset': {
                                        borderColor: 'rgba(255,255,255,0.15)',
                                      },
                                      '&:hover fieldset': {
                                        borderColor: 'rgba(255,255,255,0.25)',
                                      },
                                    },
                                    '& .MuiInputBase-input': {
                                      color: 'white',
                                    },
                                  }}
                                />
                              </Grid>
                              <Grid item xs={6} sm={2}>
                                <TextField
                                  fullWidth
                                  size="small"
                                  type="number"
                                  value={editingItemData.quantity}
                                  onChange={(e) => setEditingItemData({ ...editingItemData, quantity: parseFloat(e.target.value) })}
                                  placeholder="Qté"
                                  sx={{ 
                                    '& .MuiOutlinedInput-root': { 
                                      borderRadius: 2,
                                      bgcolor: 'rgba(255,255,255,0.05)',
                                      color: 'white',
                                      '& fieldset': {
                                        borderColor: 'rgba(255,255,255,0.15)',
                                      },
                                    },
                                    '& .MuiInputBase-input': {
                                      color: 'white',
                                    },
                                  }}
                                />
                              </Grid>
                              <Grid item xs={6} sm={2}>
                                <FormControl fullWidth size="small">
                                  <Select
                                    value={editingItemData.unit}
                                    onChange={(e) => setEditingItemData({ ...editingItemData, unit: e.target.value })}
                                    sx={{ 
                                      borderRadius: 2,
                                      bgcolor: 'rgba(255,255,255,0.05)',
                                      color: 'white',
                                      '& .MuiOutlinedInput-notchedOutline': {
                                        borderColor: 'rgba(255,255,255,0.15)',
                                      },
                                      '&:hover .MuiOutlinedInput-notchedOutline': {
                                        borderColor: 'rgba(255,255,255,0.25)',
                                      },
                                      '& .MuiSvgIcon-root': {
                                        color: 'white',
                                      },
                                    }}
                                  >
                                    {units.map(unit => (
                                      <MenuItem key={unit} value={unit}>{unit}</MenuItem>
                                    ))}
                                  </Select>
                                </FormControl>
                              </Grid>
                              <Grid item xs={6} sm={1.5}>
                                <IconButton 
                                  size="small" 
                                  color="primary"
                                  onClick={() => handleSaveEditItem(item.id)}
                                  sx={{ 
                                    bgcolor: 'primary.main',
                                    color: 'white',
                                    '&:hover': { bgcolor: 'primary.dark' }
                                  }}
                                >
                                  <SaveIcon fontSize="small" />
                                </IconButton>
                              </Grid>
                              <Grid item xs={6} sm={1.5}>
                                <IconButton 
                                  size="small"
                                  onClick={handleCancelEdit}
                                >
                                  <CloseIcon fontSize="small" />
                                </IconButton>
                              </Grid>
                            </Grid>
                          ) : (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                              <Checkbox
                                checked={item.status === 'bought'}
                                onChange={() => handleToggleItem(item)}
                                icon={<RadioButtonUncheckedIcon />}
                                checkedIcon={<CheckCircleIcon sx={{ color: '#4caf50' }} />}
                                sx={{ 
                                  p: 0,
                                  '& .MuiSvgIcon-root': { fontSize: 28 }
                                }}
                              />
                              
                              <Box 
                                sx={{ flexGrow: 1, cursor: 'pointer' }}
                                onClick={() => handleStartEditItem(item)}
                              >
                                <Typography 
                                  variant="body1"
                                  sx={{ 
                                    fontWeight: 600,
                                    textDecoration: item.status === 'bought' ? 'line-through' : 'none',
                                    color: item.status === 'bought' ? 'rgba(255,255,255,0.5)' : 'rgba(255,255,255,0.9)',
                                  }}
                                >
                                  {item.name}
                                  {item.brand && (
                                    <Typography component="span" variant="body2" sx={{ ml: 1, fontWeight: 400, color: 'rgba(255,255,255,0.6)' }}>
                                      ({item.brand})
                                    </Typography>
                                  )}
                                </Typography>
                                <Typography 
                                  variant="body2" 
                                  sx={{ 
                                    color: 'rgba(255,255,255,0.6)',
                                    mt: 0.5
                                  }}
                                >
                                  <strong>{item.quantity}</strong> {item.unit}
                                  {item.notes && ` • ${item.notes}`}
                                </Typography>
                              </Box>
                              
                              <IconButton
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation()
                                  handleDeleteItem(item.id)
                                }}
                                sx={{ 
                                  color: 'error.main',
                                  '&:hover': { 
                                    bgcolor: 'error.lighter',
                                    transform: 'scale(1.1)'
                                  },
                                  transition: 'all 0.2s'
                                }}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Box>
                          )}
                        </CardContent>
                      </ItemCard>
                    ))}
                  </List>
                  
                  {/* Add Item to This Aisle Buttons */}
                  <Box sx={{ mt: 2, display: 'flex', gap: 2 }}>
                    <AddButton
                      variant="outlined"
                      startIcon={<AddIcon />}
                      onClick={() => handleOpenAddItemToAisle(aisle)}
                      sx={{ flex: 1, borderStyle: 'dashed', borderWidth: 2 }}
                    >
                      Ajouter un produit
                    </AddButton>
                    <AddButton
                      variant="outlined"
                      startIcon={<LibraryAddIcon />}
                      onClick={() => setShowAddFromLibrary(true)}
                      sx={{ flex: 1, borderStyle: 'dashed', borderWidth: 2 }}
                    >
                      Depuis la bibliothèque
                    </AddButton>
                  </Box>
                </AccordionDetails>
              </StyledAccordion>
              ))}
            </>
          )}
        </Box>
      )}

      {/* Add New Aisle Dialog */}
      <Dialog 
        open={showAddAisleDialog} 
        onClose={() => setShowAddAisleDialog(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: { 
            borderRadius: 4,
            background: 'linear-gradient(145deg, #1e1e2e 0%, #252540 100%)',
            border: '1px solid rgba(255,255,255,0.1)',
          }
        }}
      >
        <DialogTitle sx={{ fontWeight: 700, color: 'white' }}>
          Créer un nouveau rayon
        </DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            autoFocus
            margin="dense"
            label="Nom du rayon"
            placeholder="Ex: Produits laitiers, Boulangerie..."
            value={newAisleName}
            onChange={(e) => setNewAisleName(e.target.value)}
            onKeyPress={(e) => {
              if (e.key === 'Enter' && newAisleName.trim()) {
                handleCreateAisle()
              }
            }}
            sx={{ 
              mt: 2,
              '& .MuiOutlinedInput-root': { 
                borderRadius: 3,
                bgcolor: 'rgba(255,255,255,0.05)',
                color: 'white',
              },
              '& .MuiInputLabel-root': {
                color: 'rgba(255,255,255,0.7)',
              },
              '& .MuiInputBase-input': {
                color: 'white',
              },
            }}
          />
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button 
            onClick={() => {
              setShowAddAisleDialog(false)
              setNewAisleName('')
            }}
            sx={{ borderRadius: 20 }}
          >
            Annuler
          </Button>
          <AddButton
            onClick={handleCreateAisle}
            variant="contained"
            disabled={!newAisleName.trim()}
          >
            Créer le rayon
          </AddButton>
        </DialogActions>
      </Dialog>

      {/* Add Item to Aisle Dialog */}
      <Dialog 
        open={showAddItemDialog} 
        onClose={() => {
          setShowAddItemDialog(false)
          setSelectedAisle('')
          setEditingItemData({ name: '', quantity: 1, unit: 'pcs' })
        }}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: { 
            borderRadius: 4,
            background: 'linear-gradient(145deg, #1e1e2e 0%, #252540 100%)',
            border: '1px solid rgba(255,255,255,0.1)',
          }
        }}
      >
        <DialogTitle sx={{ fontWeight: 700, color: 'white' }}>
          Ajouter un article - {selectedAisle}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Nom de l'article"
                value={editingItemData.name}
                onChange={(e) => setEditingItemData({ ...editingItemData, name: e.target.value })}
                placeholder="Ex: Lait, Pain, Tomates..."
                autoFocus
                sx={{ 
                  '& .MuiOutlinedInput-root': { 
                    borderRadius: 3,
                    bgcolor: 'rgba(255,255,255,0.05)',
                    color: 'white',
                  },
                  '& .MuiInputLabel-root': {
                    color: 'rgba(255,255,255,0.7)',
                  },
                  '& .MuiInputBase-input': {
                    color: 'white',
                  },
                }}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Quantité"
                type="number"
                value={editingItemData.quantity}
                onChange={(e) => setEditingItemData({ ...editingItemData, quantity: parseFloat(e.target.value) })}
                sx={{ 
                  '& .MuiOutlinedInput-root': { 
                    borderRadius: 3,
                    bgcolor: 'rgba(255,255,255,0.05)',
                    color: 'white',
                  },
                  '& .MuiInputLabel-root': {
                    color: 'rgba(255,255,255,0.7)',
                  },
                  '& .MuiInputBase-input': {
                    color: 'white',
                  },
                }}
              />
            </Grid>
            <Grid item xs={6}>
              <FormControl fullWidth>
                <InputLabel sx={{ color: 'rgba(255,255,255,0.7)' }}>Unité</InputLabel>
                <Select
                  value={editingItemData.unit}
                  label="Unité"
                  onChange={(e) => setEditingItemData({ ...editingItemData, unit: e.target.value })}
                  sx={{ 
                    borderRadius: 3,
                    bgcolor: 'rgba(255,255,255,0.05)',
                    color: 'white',
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: 'rgba(255,255,255,0.15)',
                    },
                    '& .MuiSvgIcon-root': {
                      color: 'white',
                    },
                  }}
                >
                  {units.map(unit => (
                    <MenuItem key={unit} value={unit}>{unit}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button 
            onClick={() => {
              setShowAddItemDialog(false)
              setSelectedAisle('')
              setEditingItemData({ name: '', quantity: 1, unit: 'pcs' })
            }}
            sx={{ borderRadius: 20 }}
          >
            Annuler
          </Button>
          <AddButton
            onClick={() => handleAddItem()}
            variant="contained"
            startIcon={<AddIcon />}
            disabled={!editingItemData.name.trim()}
          >
            Ajouter
          </AddButton>
        </DialogActions>
      </Dialog>

      {/* Add from Library Dialog */}
      <Dialog
        open={showAddFromLibrary}
        onClose={() => setShowAddFromLibrary(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Ajouter des Articles depuis la Bibliothèque</DialogTitle>
        <DialogContent>
          <List>
            {libraryItems.map((item) => (
              <ListItem key={item.id}>
                <Checkbox
                  checked={selectedLibraryItems.includes(item.id)}
                  onChange={(e) => {
                    if (e.target.checked) {
                      setSelectedLibraryItems([...selectedLibraryItems, item.id])
                    } else {
                      setSelectedLibraryItems(selectedLibraryItems.filter(id => id !== item.id))
                    }
                  }}
                />
                <ListItemText
                  primary={item.name}
                  secondary={`${item.default_quantity} ${item.default_unit} - ${item.default_aisle || 'Aucun rayon'}`}
                />
              </ListItem>
            ))}
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowAddFromLibrary(false)}>Annuler</Button>
          <Button 
            onClick={handleAddFromLibrary}
            variant="contained"
            disabled={selectedLibraryItems.length === 0}
          >
            Ajouter la Sélection ({selectedLibraryItems.length})
          </Button>
        </DialogActions>
      </Dialog>

      {/* Rationalization Preview Dialog */}
      <Dialog
        open={showRationalizeDialog}
        onClose={() => {
          if (!rationalizeLoading) {
            setShowRationalizeDialog(false)
            setRationalizePreview(null)
          }
        }}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: { 
            borderRadius: 4,
            background: 'linear-gradient(145deg, #1e1e2e 0%, #252540 100%)',
            border: '1px solid rgba(255,255,255,0.1)',
            maxHeight: '80vh'
          }
        }}
      >
        <DialogTitle sx={{ fontWeight: 700, color: 'white', display: 'flex', alignItems: 'center', gap: 2 }}>
          <AutoFixHighIcon sx={{ color: '#f093fb' }} />
          Rationalisation IA des Articles
        </DialogTitle>
        <DialogContent>
          {rationalizeLoading ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 6 }}>
              <CircularProgress size={60} sx={{ color: '#f093fb', mb: 3 }} />
              <Typography variant="h6" sx={{ color: 'white', mb: 1 }}>
                Analyse en cours...
              </Typography>
              <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.6)' }}>
                L'IA est en train d'optimiser votre liste de courses
              </Typography>
            </Box>
          ) : rationalizePreview ? (
            <Box sx={{ mt: 2 }}>
              <Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.8)', mb: 3 }}>
                L'IA a analysé vos articles et propose les optimisations suivantes :
              </Typography>

              <Grid container spacing={3}>
                {/* Original Items */}
                <Grid item xs={12} md={6}>
                  <StyledPaper sx={{ p: 2, height: '100%' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                      <Typography variant="h6" sx={{ color: 'white', fontWeight: 600 }}>
                        📋 Articles Originaux
                      </Typography>
                      <Chip 
                        label={rationalizePreview.original_items?.length || 0}
                        size="small"
                        sx={{ 
                          bgcolor: 'rgba(255,255,255,0.15)',
                          color: 'white',
                          fontWeight: 600
                        }}
                      />
                    </Box>
                    <List sx={{ maxHeight: 400, overflow: 'auto' }}>
                      {rationalizePreview.original_items?.map((item, index) => (
                        <ListItem 
                          key={index}
                          sx={{ 
                            bgcolor: 'rgba(255,255,255,0.05)',
                            borderRadius: 2,
                            mb: 1,
                            border: '1px solid rgba(255,255,255,0.08)'
                          }}
                        >
                          <ListItemText
                            primary={
                              <Typography sx={{ color: 'white', fontWeight: 500 }}>
                                {item.name}
                              </Typography>
                            }
                            secondary={
                              <Typography sx={{ color: 'rgba(255,255,255,0.6)' }}>
                                {item.quantity} {item.unit}
                              </Typography>
                            }
                          />
                        </ListItem>
                      ))}
                    </List>
                  </StyledPaper>
                </Grid>

                {/* Rationalized Items */}
                <Grid item xs={12} md={6}>
                  <StyledPaper sx={{ p: 2, height: '100%', border: '2px solid #f093fb' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                      <Typography variant="h6" sx={{ color: 'white', fontWeight: 600 }}>
                        ✨ Articles Optimisés
                      </Typography>
                      <Chip 
                        label={rationalizePreview.rationalized_items?.length || 0}
                        size="small"
                        sx={{ 
                          bgcolor: '#f093fb',
                          color: 'white',
                          fontWeight: 600
                        }}
                      />
                    </Box>
                    <List sx={{ maxHeight: 400, overflow: 'auto' }}>
                      {rationalizePreview.rationalized_items?.map((item, index) => (
                        <ListItem 
                          key={index}
                          sx={{ 
                            bgcolor: 'rgba(240, 147, 251, 0.1)',
                            borderRadius: 2,
                            mb: 1,
                            border: '1px solid rgba(240, 147, 251, 0.3)'
                          }}
                        >
                          <ListItemText
                            primary={
                              <Typography sx={{ color: 'white', fontWeight: 600 }}>
                                {item.normalized_name || item.name}
                              </Typography>
                            }
                            secondary={
                              <Box>
                                <Typography sx={{ color: 'rgba(255,255,255,0.7)' }}>
                                  {item.quantity} {item.unit}
                                </Typography>
                                {item.category && (
                                  <Chip 
                                    label={item.category}
                                    size="small"
                                    sx={{ 
                                      mt: 0.5,
                                      bgcolor: 'rgba(240, 147, 251, 0.2)',
                                      color: '#f093fb',
                                      fontSize: '0.7rem'
                                    }}
                                  />
                                )}
                              </Box>
                            }
                          />
                        </ListItem>
                      ))}
                    </List>
                  </StyledPaper>
                </Grid>
              </Grid>

              <Box sx={{ mt: 3, p: 2, bgcolor: 'rgba(240, 147, 251, 0.1)', borderRadius: 2, border: '1px solid rgba(240, 147, 251, 0.3)' }}>
                <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.8)', display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CompareArrowsIcon sx={{ color: '#f093fb' }} />
                  <strong>Améliorations :</strong> Articles dupliqués fusionnés, unités standardisées, catégories attribuées automatiquement
                </Typography>
              </Box>
            </Box>
          ) : (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography sx={{ color: 'rgba(255,255,255,0.6)' }}>
                Aucune prévisualisation disponible
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button 
            onClick={() => {
              setShowRationalizeDialog(false)
              setRationalizePreview(null)
            }}
            disabled={rationalizeLoading}
            sx={{ borderRadius: 20, color: 'white' }}
          >
            Annuler
          </Button>
          <AddButton
            onClick={handleApplyRationalization}
            variant="contained"
            disabled={rationalizeLoading || !rationalizePreview}
            startIcon={rationalizeLoading ? <CircularProgress size={20} /> : <SaveIcon />}
            sx={{ 
              background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
              '&:hover': {
                background: 'linear-gradient(135deg, #f5a3ff 0%, #ff6b7f 100%)',
              }
            }}
          >
            {rationalizeLoading ? 'Application...' : 'Confirmer les changements'}
          </AddButton>
        </DialogActions>
      </Dialog>
      </Container>
    </Box>
  )
}

export default ShoppingListDetail
